import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, QrCode, ChevronRight, Link2 } from "lucide-react";
import { BackLink, DetailHeader, Panel, InfoGrid, ActionButton, AddButton, Empty, ConfirmDelete } from "@/components/Bits";
import { EquipamentoFormDialog } from "@/components/EquipamentoFormDialog";
import { HistoricoSection } from "@/components/HistoricoSection";
import { OSHistory } from "@/components/OSHistory";
import { DocumentosPanel } from "@/components/DocumentosPanel";
import { TIPOS, parseField, fieldValue, tipoIcon, EQ_STATUS } from "@/config/equipamentos";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";

const Chain = ({ e }) => {
  const steps = [
    e.cliente && { to: `/clientes/${e.cliente.cliente_id}`, label: e.cliente.nome },
    e.fazenda && { to: `/fazendas/${e.fazenda.fazenda_id}`, label: e.fazenda.nome },
    ...e.cadeia.map((c) => ({ to: `/equipamentos/${c.equipamento_id}`, label: c.titulo })),
    { label: e.titulo },
  ].filter(Boolean);
  return (
    <nav className="flex flex-wrap items-center gap-1 text-sm" data-testid="equip-chain">
      {steps.map((s, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-4 w-4 text-[#5C6B66]" />}
          {s.to ? <Link to={s.to} className="px-3 py-2 rounded-lg bg-[#F4F1EA] hover:bg-[#E8F5E9] font-medium">{s.label}</Link>
            : <span className="px-3 py-2 rounded-lg bg-[#1E4D2B] text-white font-semibold">{s.label}</span>}
        </span>
      ))}
    </nav>
  );
};

export default function EquipamentoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: e, reload } = useFetch(`/equipamentos/${id}`);
  const [edit, setEdit] = useState(false);
  const [child, setChild] = useState(false);
  const [del, setDel] = useState(false);
  if (!e || e.equipamento_id !== id) return null;
  const staff = user.role !== "tecnico";
  const isAdmin = user.role === "admin";
  const tipo = TIPOS[e.tipo];

  const remove = async () => {
    try { await api.delete(`/equipamentos/${id}`); toast.success("Equipamento excluído"); navigate("/equipamentos"); }
    catch (err) { toast.error(errMsg(err)); setDel(false); }
  };

  return (
    <div data-testid="equip-detail-page">
      <BackLink to="/equipamentos" label="Equipamentos" />
      <DetailHeader eyebrow={tipo.label} title={e.titulo} subtitle={`${e.codigo} · ${EQ_STATUS[e.status]}`} icon={tipoIcon(e.tipo)} actions={<>
        {staff && <ActionButton icon={Pencil} onClick={() => setEdit(true)} data-testid="equip-edit-button">Editar</ActionButton>}
        {user.role === "admin" && <ActionButton icon={Trash2} variant="danger" onClick={() => setDel(true)} data-testid="equip-delete-button">Excluir</ActionButton>}
      </>} />
      <div className="space-y-5">
        <Panel title="Relacionamento" testId="equip-relacao"><Chain e={e} /></Panel>
        <div className="grid lg:grid-cols-[1fr_320px] gap-5">
          <Panel title="Dados técnicos" testId="equip-info">
            <InfoGrid items={[...tipo.fields.map(parseField).map((f) => [f.label, fieldValue(e, f.key)]), ["Situação", EQ_STATUS[e.status]], ["Observações", e.observacoes]]} />
          </Panel>
          <Panel title="Identificador" testId="equip-identificador">
            <div className="rounded-2xl border-2 border-dashed border-[#C86D51]/50 bg-[#F4F1EA] p-5 text-center">
              <QrCode className="h-12 w-12 mx-auto text-[#1E4D2B]" />
              <p className="mt-3 font-display text-2xl font-extrabold tracking-wider" data-testid="equip-codigo">{e.codigo}</p>
              <p className="mt-2 text-[11px] text-[#5C6B66] break-all" data-testid="equip-qr-token">ID QR: {e.qr_token}</p>
              <p className="mt-2 text-xs text-[#5C6B66]">Pronto para gerar o QR Code da máquina.</p>
            </div>
          </Panel>
        </div>
        <Panel title={`Equipamentos vinculados (${e.filhos.length})`} testId="equip-filhos"
          action={staff && <AddButton small onClick={() => setChild(true)} data-testid="equip-add-child">Vincular novo</AddButton>}>
          {e.filhos.length === 0 && <Empty>Nenhum equipamento vinculado.</Empty>}
          <div className="space-y-2">
            {e.filhos.map((k) => { const I = tipoIcon(k.tipo); return (
              <Link key={k.equipamento_id} to={`/equipamentos/${k.equipamento_id}`} data-testid={`equip-child-${k.equipamento_id}`}
                className="min-w-0 flex items-center gap-3 min-h-[56px] px-3 rounded-xl border border-[#E2DDD3] hover:border-[#1E4D2B]">
                <Link2 className="h-4 w-4 text-[#5C6B66]" /><I className="h-5 w-5 text-[#1E4D2B]" />
                <span className="font-semibold flex-1 truncate">{k.titulo}</span><span className="text-xs text-[#5C6B66]">{k.codigo}</span>
              </Link>); })}
          </div>
        </Panel>
        {isAdmin && <HistoricoSection eq={e} canAdd />}
        <OSHistory filter={{ equipamento_id: id }} newQuery={{ cliente_id: e.cliente_id, fazenda_id: e.fazenda_id, equipamento_id: id }} />
        {isAdmin && <DocumentosPanel filter={{ equipamento_id: id }} fixed={{ cliente_id: e.cliente_id, fazenda_id: e.fazenda_id, equipamento_id: id }} />}
      </div>
      <EquipamentoFormDialog open={edit} onOpenChange={setEdit} editing={e} onSaved={reload} />
      <EquipamentoFormDialog open={child} onOpenChange={setChild} fazendaId={e.fazenda_id} parentId={id} onSaved={reload} />
      <ConfirmDelete open={del} onOpenChange={setDel} title="Excluir equipamento?" description="O histórico será removido e os equipamentos vinculados ficarão soltos na fazenda." onConfirm={remove} />
    </div>
  );
}
